export function TimeValueOfMoney() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            The Time Value of Money: Why Waiting Costs You
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Even if renovations increased your property value, the time you spend waiting has a real financial cost that most owners completely overlook.
          </p>
        </div>

        {/* Comparison Infographic */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-10 mb-16 border-2 border-gray-200">
          <h3 className="text-3xl font-bold mb-10 text-center">18-Month Opportunity Cost Analysis</h3>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Renovation Path */}
            <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-red-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                  <i className="ri-hammer-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold">Renovation Path</h4>
              </div>
              <div className="space-y-4 mb-8">
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Month 0: Start renovations</span>
                  <strong className="text-red-600">$0</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Months 1-12: Construction</span>
                  <strong className="text-red-600">-$1,200,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Months 13-15: Lease-up</span>
                  <strong className="text-red-600">-$75,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Months 16-18: Market & sell</span>
                  <strong className="text-red-600">-$45,000</strong>
                </div>
                <div className="flex justify-between pt-4 border-t-2 border-red-400">
                  <span className="font-bold">Month 18: Finally get paid</span>
                  <strong className="text-xl text-red-600">$1,425,000</strong>
                </div>
                <div className="flex justify-between pt-4 border-t-2 border-red-400">
                  <span className="font-bold">Net after costs</span>
                  <strong className="text-xl text-red-600">$105,000</strong>
                </div>
              </div>
              <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What you could have earned:</strong> If you had sold AS IS and invested $1.2M at 8% annual return for 18 months
                </p>
                <p className="text-2xl font-bold text-red-600">Lost opportunity: $144,000</p>
              </div>
            </div>

            {/* AS IS Path */}
            <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-teal-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center">
                  <i className="ri-hand-coin-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold">Sell AS IS Path</h4>
              </div>
              <div className="space-y-4 mb-8">
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Month 0: Contact us</span>
                  <strong className="text-teal-600">$0</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Month 1: Close sale</span>
                  <strong className="text-teal-600">$1,200,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Months 2-18: Invest proceeds</span>
                  <strong className="text-teal-600">+$144,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">No renovation costs</span>
                  <strong className="text-teal-600">+$1,200,000</strong>
                </div>
                <div className="flex justify-between pt-4 border-t-2 border-teal-400">
                  <span className="font-bold">Month 18: Total value</span>
                  <strong className="text-xl text-teal-600">$2,544,000</strong>
                </div>
                <div className="flex justify-between pt-4 border-t-2 border-teal-400">
                  <span className="font-bold">Net benefit</span>
                  <strong className="text-xl text-teal-600">$1,344,000</strong>
                </div>
              </div>
              <div className="bg-teal-50 rounded-lg p-6 border border-teal-200">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Your advantage:</strong> Immediate liquidity plus investment returns over 18 months
                </p>
                <p className="text-2xl font-bold text-teal-600">Total advantage: $1,239,000</p>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <p className="text-2xl font-bold text-gray-800 mb-4">
              By selling AS IS, you're <span className="text-teal-600">$1.2+ million better off</span> after 18 months
            </p>
            <p className="text-lg text-gray-700">
              This doesn't even account for the stress, risk, and hassle you avoided. Time is money, and selling AS IS gives you both.
            </p>
          </div>
        </div>

        {/* Additional Benefits */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-8 border-2 border-teal-200">
            <div className="w-14 h-14 bg-teal-600 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-line-chart-line text-2xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4">Reinvestment Opportunities</h3>
            <p className="text-gray-700 leading-relaxed">
              With cash in hand immediately, you can take advantage of other investment opportunities, market timing, or personal needs without waiting 18 months.
            </p>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-8 border-2 border-teal-200">
            <div className="w-14 h-14 bg-teal-600 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-shield-check-line text-2xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4">Risk Elimination</h3>
            <p className="text-gray-700 leading-relaxed">
              Market conditions can change dramatically in 18 months. Selling AS IS locks in your value today and eliminates exposure to market downturns.
            </p>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-8 border-2 border-teal-200">
            <div className="w-14 h-14 bg-teal-600 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-mental-health-line text-2xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4">Peace of Mind</h3>
            <p className="text-gray-700 leading-relaxed">
              The mental and emotional cost of managing a renovation for 18 months is immeasurable. Selling AS IS gives you immediate relief and freedom.
            </p>
          </div>
        </div>

        {/* Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img
            src="https://public.readdy.ai/ai/img_res/59c399b9423ec6ffbbd3d8fa2c80ff26.jpg"
            alt="Time value of money in real estate"
            className="w-full h-[500px] object-cover object-top"
          />
        </div>
      </div>
    </section>
  );
}